/**
 * Mini PDP Modal
 * 
 * Opens a quick-view modal when clicking on bundle-item-card elements.
 * Fetches product data via the Section Rendering API.
 * 
 * Mobile features:
 * - Swipe down to dismiss
 * - Slide-up entrance animation
 */

import Swiper from 'swiper/bundle';

// Unique name to avoid duplicate declaration when bundled with other modules (e.g. core.js)
const MINI_PDP_MOBILE_PX = 750;

// Swipe configuration
const SWIPE_THRESHOLD = 100;      // Minimum distance to trigger dismiss
const SWIPE_VELOCITY_THRESHOLD = 0.5; // Minimum velocity (px/ms) to dismiss regardless of distance
const DRAG_RESISTANCE = 0.5;      // Resistance when dragging up (0-1)

class MiniPdpModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.currentProductHandle = null;
    this.gallerySwiper = null;
    this.triggerElement = null; // For focus restoration on close
    
    // Touch/swipe state
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.currentTranslateY = 0;
    this.isDragging = false;
  }

  connectedCallback() {
    this.cacheElements();
    this.attachListeners();
    console.log('[MiniPdpModal] Initialized');
  }

  disconnectedCallback() {
    this.detachListeners();
  }

  cacheElements() {
    this.overlay = this.querySelector('[data-mini-pdp-overlay]');
    this.content = this.querySelector('[data-mini-pdp-content]');
    this.closeBtn = this.querySelector('[data-mini-pdp-close]');
    this.body = this.querySelector('[data-mini-pdp-body]');
    this.loadingState = this.querySelector('[data-mini-pdp-loading]');
    this.dragHandle = this.querySelector('[data-mini-pdp-drag-handle]');
    this.titleEl = this.querySelector('[data-mini-pdp-title]');
  }

  /**
   * Check if we're on mobile viewport
   */
  isMobile() {
    return window.innerWidth < MINI_PDP_MOBILE_PX;
  }

  attachListeners() {
    // Close button
    this.closeBtn?.addEventListener('click', this.close.bind(this));
    
    // Overlay click to close
    this.overlay?.addEventListener('click', this.close.bind(this));
    
    // Escape key to close
    this.handleEscape = this.handleEscape.bind(this);
    document.addEventListener('keydown', this.handleEscape);
    
    // Listen for clicks on bundle-item-card elements
    this.handleCardClick = this.handleCardClick.bind(this);
    document.addEventListener('click', this.handleCardClick);
    
    // Mobile swipe-to-dismiss handlers
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    
    // Attach to content panel (the draggable element)
    if (this.content) {
      this.content.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.content.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      this.content.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }
  }

  detachListeners() {
    this.closeBtn?.removeEventListener('click', this.close.bind(this));
    this.overlay?.removeEventListener('click', this.close.bind(this));
    document.removeEventListener('keydown', this.handleEscape);
    document.removeEventListener('click', this.handleCardClick);
    
    if (this.content) {
      this.content.removeEventListener('touchstart', this.handleTouchStart);
      this.content.removeEventListener('touchmove', this.handleTouchMove);
      this.content.removeEventListener('touchend', this.handleTouchEnd);
    }
  }
  
  // ============================================================================
  // Mobile Swipe-to-Dismiss Handlers
  // ============================================================================
  
  /**
   * Handle touch start - record initial position
   */
  handleTouchStart(event) {
    if (!this.isMobile() || !this.isOpen) return;
    
    const touch = event.touches[0];
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.currentTranslateY = 0;
    
    // Check if touch started on the drag handle or top bar area
    const target = event.target;
    const isOnDragHandle = target.closest('[data-mini-pdp-drag-handle]') || 
                          target.closest('.mini-pdp-modal__top-bar');
    
    // Check if content is scrolled to top
    const scrollTop = this.body?.scrollTop || 0;
    const canStartDrag = isOnDragHandle || scrollTop <= 0;
    
    this.canDrag = canStartDrag;
    this.isDragging = false;
    
    // Disable transition during drag for responsive feel
    if (this.content) {
      this.content.style.transition = 'none';
    }
  }
   
  /**
   * Handle touch move - apply transform based on drag distance
   */
  handleTouchMove(event) {
    if (!this.isMobile() || !this.isOpen || !this.canDrag) return;
    
    const touch = event.touches[0];
    const deltaY = touch.clientY - this.touchStartY;
    
    // Only allow dragging downward (or with resistance upward)
    if (deltaY > 0) {
      // Dragging down - allow freely
      this.currentTranslateY = deltaY;
      this.isDragging = true;
      
      // Prevent scroll while dragging
      event.preventDefault();
      
      // Apply transform
      if (this.content) {
        this.content.style.transform = `translateY(${deltaY}px)`;
      }
      
      // Fade overlay based on drag progress
      if (this.overlay) {
        const progress = Math.min(deltaY / 300, 1);
        this.overlay.style.opacity = 1 - (progress * 0.6);
      }
    } else if (deltaY < 0 && this.isDragging) {
      // Dragging up after already started dragging - apply resistance
      const resistedDelta = deltaY * DRAG_RESISTANCE;
      this.currentTranslateY = resistedDelta;
      
      event.preventDefault();
      
      if (this.content) {
        this.content.style.transform = `translateY(${resistedDelta}px)`;
      }
    }
  }
  
  /**
   * Handle touch end - determine if we should dismiss or snap back
   */
  handleTouchEnd(event) {
    if (!this.isMobile() || !this.isOpen) return;
    
    // Re-enable transitions
    if (this.content) {
      this.content.style.transition = '';
    }
    if (this.overlay) {
      this.overlay.style.transition = '';
    }
    
    if (!this.isDragging) {
      this.resetDragState();
      return;
    }
    
    // Calculate velocity
    const touchDuration = Date.now() - this.touchStartTime;
    const velocity = this.currentTranslateY / touchDuration;
    
    // Determine if we should dismiss
    const shouldDismiss = 
      this.currentTranslateY > SWIPE_THRESHOLD || 
      (velocity > SWIPE_VELOCITY_THRESHOLD && this.currentTranslateY > 30);
    
    if (shouldDismiss) {
      this.dismissWithAnimation();
    } else {
      this.snapBack();
    }
    
    this.resetDragState();
  }
  
  /**
   * Reset drag state variables
   */
  resetDragState() {
    this.isDragging = false;
    this.canDrag = false;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.currentTranslateY = 0;
  }
  
  /**
   * Animate the modal dismissing downward
   */
  dismissWithAnimation() {
    if (this.content) {
      // Get the content height to animate off screen
      const contentHeight = this.content.offsetHeight;
      
      // Apply dismiss animation
      this.content.style.transition = 'transform 0.25s ease-out';
      this.content.style.transform = `translateY(${contentHeight}px)`;
    }
    
    if (this.overlay) {
      this.overlay.style.transition = 'opacity 0.25s ease-out';
      this.overlay.style.opacity = '0';
    }
    
    // Close after animation completes
    setTimeout(() => {
      this.close();
      // Reset inline styles
      if (this.content) {
        this.content.style.transform = '';
        this.content.style.transition = '';
      }
      if (this.overlay) {
        this.overlay.style.opacity = '';
        this.overlay.style.transition = '';
      }
    }, 250);
  }
  
  /**
   * Snap the modal back to its original position
   */
  snapBack() {
    if (this.content) {
      this.content.style.transition = 'transform 0.2s ease-out';
      this.content.style.transform = 'translateY(0)';
    }
    
    if (this.overlay) {
      this.overlay.style.transition = 'opacity 0.2s ease-out';
      this.overlay.style.opacity = '1';
    }
    
    // Clean up inline styles after animation
    setTimeout(() => {
      if (this.content) {
        this.content.style.transform = '';
        this.content.style.transition = '';
      }
      if (this.overlay) {
        this.overlay.style.opacity = '';
        this.overlay.style.transition = '';
      }
    }, 200);
  }

  handleCardClick(event) {
    const card = event.target.closest('[data-open-mini-pdp]');
    if (!card) return;
    
    event.preventDefault();
    
    // Store the trigger element for focus restoration
    this.triggerElement = card;
    
    const productHandle = card.dataset.productHandle;
    const variantId = card.dataset.variantId;
    
    // Capture retail price from card data attributes (pre-formatted by Liquid)
    this.retailPrice = card.dataset.comparePriceFormatted || null;
    
    if (productHandle) {
      this.open(productHandle, variantId);
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  async open(productHandle, variantId = null) {
    if (this.isOpen && this.currentProductHandle === productHandle) {
      return;
    }

    this.currentProductHandle = productHandle;
    this.isOpen = true;
    
    // Update aria-hidden for screen readers
    this.setAttribute('aria-hidden', 'false');
    
    // On mobile, set initial off-screen position for slide-up animation
    if (this.isMobile() && this.content) {
      this.content.style.transform = 'translateY(100%)';
      this.content.style.transition = 'none';
    }
    
    // Show modal with loading state
    this.classList.add('is-open');
    this.showLoading(true);
    document.body.classList.add('overflow-hidden');
    
    // Move focus to the close button for keyboard accessibility
    // Delay slightly to ensure DOM is ready
    setTimeout(() => {
      this.closeBtn?.focus();
    }, 100);
    
    // Trigger the slide-up animation on mobile
    if (this.isMobile() && this.content) {
      // Force reflow to ensure the initial position is applied
      void this.content.offsetHeight;
      
      // Animate up
      this.content.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
      this.content.style.transform = 'translateY(0)';
      
      // Clean up inline styles after animation
      setTimeout(() => {
        if (this.content) {
          this.content.style.transform = '';
          this.content.style.transition = '';
        }
      }, 350);
    }
    
    try {
      // Build URL with optional variant parameter
      let url = `/products/${productHandle}?section_id=mini-pdp-modal`;
      if (variantId) {
        url += `&variant=${variantId}`;
      }
      
      // Fetch product section via Section Rendering API
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }
      
      const html = await response.text();
      this.renderContent(html);
      
    } catch (error) {
      console.error('[MiniPdpModal] Error loading product:', error);
      this.renderError();
    } finally {
      this.showLoading(false);
    }
  }

  close() {
    // If already closing via swipe animation, don't double-trigger
    if (!this.isOpen) return;
    
    this.isOpen = false;
    this.currentProductHandle = null;
    this.retailPrice = null;
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    
    // Update aria-hidden for screen readers
    this.setAttribute('aria-hidden', 'true');
    
    // Reset accessible title to default
    if (this.titleEl) {
      this.titleEl.textContent = 'Product Details';
    }
    
    // Reset any inline styles from dragging
    if (this.content) {
      this.content.style.transform = '';
      this.content.style.transition = '';
    }
    if (this.overlay) {
      this.overlay.style.opacity = '';
      this.overlay.style.transition = '';
    }
    
    // Destroy gallery Swiper
    this.destroyGallerySwiper();
    
    // Return focus to the trigger element for keyboard accessibility
    if (this.triggerElement) {
      this.triggerElement.focus();
      this.triggerElement = null;
    }
  }

  showLoading(show) {
    if (this.loadingState) {
      this.loadingState.style.display = show ? 'flex' : 'none';
    }
  }

  renderContent(html) {
    if (this.body) {
      // Reset body state before injecting content
      this.body.classList.remove('is-visible');
      
      // Parse the returned HTML and extract the content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const modalContent = doc.querySelector('[data-mini-pdp-modal]');
      
      if (modalContent) {
        this.body.innerHTML = modalContent.innerHTML;
      } else {
        // Fallback: use the entire response
        this.body.innerHTML = html;
      }
      
      // Update accessible title from product name
      this.updateAccessibleTitle();
      
      // Force reflow, then fade in content and init Swiper
      void this.body.offsetHeight;
      
      requestAnimationFrame(() => {
        this.body.classList.add('is-visible');
        this.initGallerySwiper();
        this.updateBundlePricing();
      });
    }
  }
  
  /**
   * Update the accessible title element with the product name
   * Looks for a product title in the loaded content
   */
  updateAccessibleTitle() {
    if (!this.titleEl) return;
    
    // Try to find a product title in the loaded content
    const productTitle = this.body?.querySelector('[data-product-title]')?.textContent?.trim() ||
                         this.body?.querySelector('.mini-pdp__title')?.textContent?.trim() ||
                         this.body?.querySelector('h1, h2, h3')?.textContent?.trim() ||
                         'Product Details';
    
    this.titleEl.textContent = productTitle;
    console.log('[MiniPdpModal] Updated accessible title:', productTitle);
  }
  
  initGallerySwiper() {
    // Destroy existing instance if any
    if (this.gallerySwiper) {
      this.gallerySwiper.destroy(true, true);
      this.gallerySwiper = null;
    }
    
    const galleryEl = this.body.querySelector('[data-mini-pdp-gallery]');
    if (!galleryEl) {
      console.log('[MiniPdpModal] No gallery element found');
      return;
    }
    
    console.log('[MiniPdpModal] Initializing gallery Swiper');
    
    this.gallerySwiper = new Swiper(galleryEl, {
      slidesPerView: 2.25,
      spaceBetween: 8,
      grabCursor: true,
      freeMode: {
        enabled: true,
        sticky: false,
      },
    });
    
    console.log('[MiniPdpModal] Gallery Swiper initialized:', this.gallerySwiper);
  }
  
  destroyGallerySwiper() {
    if (this.gallerySwiper) {
      this.gallerySwiper.destroy(true, true);
      this.gallerySwiper = null;
    }
  }

  /**
   * Inject variant-specific retail price into the modal price row.
   * Uses the pre-formatted price captured from the trigger card's data attributes
   * to ensure the modal matches the card's pricing.
   */
  updateBundlePricing() {
    if (!this.retailPrice) return;
    
    const priceRow = this.body?.querySelector('[data-mini-pdp-price-row]');
    if (!priceRow) return;
    
    priceRow.replaceChildren();
    const labelSpan = document.createElement('span');
    labelSpan.className = 'mini-pdp-modal__price--label';
    labelSpan.textContent = 'Retail Value';
    const priceSpan = document.createElement('span');
    priceSpan.className = 'mini-pdp-modal__price';
    priceSpan.textContent = this.retailPrice;
    priceRow.append(labelSpan, priceSpan);
  }

  renderError() {
    if (this.body) {
      this.body.innerHTML = `
        <div class="mini-pdp-modal__error">
          <p>Unable to load product details.</p>
          <button type="button" onclick="this.closest('mini-pdp-modal').close()">Close</button>
        </div>
      `;
    }
  }
}

// Register the custom element
if (!customElements.get('mini-pdp-modal')) {
  customElements.define('mini-pdp-modal', MiniPdpModal);
}

// Export for external access if needed
window.MiniPdpModal = MiniPdpModal;

